import { NgModule, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, inject } from '@angular/core'; 
import { provideAppInitializer } from '@angular/core'; 
import { BrowserModule } from '@angular/platform-browser';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { environment } from '@environment/environment';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { Amplify } from 'aws-amplify';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader, TRANSLATE_HTTP_LOADER_CONFIG } from '@ngx-translate/http-loader';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import localeUk from '@angular/common/locales/uk';

// NgRx State Module (centralized state management configuration)
import { StateModule } from './store/state.module';

import { AppComponent } from '@app/app.component';
import { AppRoutingModule } from '@app/app-routing.module';
import { SharedModule } from '@app/shared/modules/shared.module';
import { ToastNotificationService } from '@app/core/infrastructure';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { AvatarBoxComponent } from './components/avatar-box/avatar-box.component';
import { ControlHeaderComponent } from './components/control-header/control-header.component';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { ConfirmationDialogComponent } from './popups/confirmation-dialog/confirmation-dialog.component';
import { ChangePasswordDialogComponent } from './popups/change-password-dialog/change-password-dialog.component';
import { FeedbackDialogComponent } from './popups/feedback-dialog/feedback-dialog.component';
import { LoadingContainerComponent } from './components/loading-container/loading-container.component';
import { registerBundledIcons } from './core/icons/icons.config';


// TranslateHttpLoader v17+ uses dependency injection with TRANSLATE_HTTP_LOADER_CONFIG
// No factory function needed - the loader uses inject() internally

// Register all supported locales for DatePipe and other Angular pipes
registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeIt, 'it');
registerLocaleData(localePt, 'pt');
registerLocaleData(localeUk, 'uk');

export function initializeIconifyIcon() {
  // Import iconify-icon and register bundled icons to avoid API calls
  return import('iconify-icon').then(() => {
    registerBundledIcons();
  }).catch(err => console.warn('Failed to load iconify-icon:', err));
}

export function configureAmplifyInitializer() { 
  // Determine if we're running in a mobile app environment
  const isMobile = (window as any).Capacitor && (window as any).Capacitor.isNativePlatform();
  
  // Set appropriate redirect URLs based on environment
  const getRedirectUrls = () => {
    if (isMobile) {
      // For mobile, include both custom scheme AND web URL as fallbacks
      // This ensures Amplify can handle the OAuth callback properly
      return {
        redirectSignIn: [
          environment.oauth.redirectUri.mobile,
          environment.oauth.redirectUri.web
        ],
        redirectSignOut: [
          environment.oauth.redirectUri.mobile.replace('/auth/callback', '/login'),
          environment.oauth.redirectUri.web.replace('/auth/callback', '/login')
        ]
      };
    } else {
      // For web, use HTTPS URLs from environment
      return {
        redirectSignIn: [environment.oauth.redirectUri.web],
        redirectSignOut: [environment.oauth.redirectUri.web.replace('/auth/callback', '/login')]
      };
    }
  };

  const redirectUrls = getRedirectUrls();

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: environment.oauth.userPoolId, 
        userPoolClientId: environment.oauth.clientId, 
        loginWith: { 
          oauth: {
            domain: environment.oauth.domain, 
            scopes: environment.oauth.scopes,
            redirectSignIn: redirectUrls.redirectSignIn, 
            redirectSignOut: redirectUrls.redirectSignOut,      
            responseType: 'code'
          }
        }
      }
    }
  });
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    // NgRx State Management - MUST come early, before other modules that depend on it
    StateModule,
    IonicModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader
      }
    }),
    AppRoutingModule,
    SharedModule,
    SidenavComponent,
    AvatarBoxComponent,
    ControlHeaderComponent,
    ConfirmationDialogComponent,
    ChangePasswordDialogComponent,
    FeedbackDialogComponent,
    LoadingContainerComponent,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: './assets/i18n/',
        suffix: '.json'
      }
    },
    ToastNotificationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    provideAppInitializer(configureAmplifyInitializer),
    provideAppInitializer(initializeIconifyIcon),
    // Sentry Error Handler and Tracing (conditional for staging)
    ...(environment.sentry?.enabled ? [
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        inject(Sentry.TraceService);
      }),
    ] : [])
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule { }